import { Link } from 'react-router-dom';

import {
  AppstoreOutlined,
  DollarOutlined,
  FileTextOutlined,
  GroupOutlined,
  InfoCircleOutlined as InfoSolidIcon,
  LinkOutlined,
  MessageOutlined,
  RightOutlined,
  SendOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { Button, Input, Spin, Tooltip } from 'antd';

import MyCollapse from 'components/Collapse';
import CustomCopyToClipboard from 'components/CustomCopyToClipboard';
import { functions } from 'helpers';
import { constants } from 'helpers/constants';
import { EChargeType } from 'interfaces/IEditProduct';
import { HOST_ORBITA, URL_SELECT, VERTICAL_URL } from 'settings';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import { ProductChangeHistory } from './ProductChangeHistory';
import QuickViewLinks from './QuickviewLinks';

type Props = {
  statusProduct: 'active' | 'inactive' | 'filed' | 'draft' | ' ';
  productDataReceive: any;
  physicalProductPending: boolean;
  copiedCheckout: () => void;
  handleChange: (props: any, event: any) => void;
  inputInviteAffiliate: string;
  productData: any;
  isValid: (field: any) => boolean;
  handleClickInviteAffiliate: () => void;
  isInvitingAffiliate: boolean;
  errorFor: (field: any) => any;
  handleClickGetAffiliatesProductStatusSummary: () => void;
  isLoadingAffiliatesSummary: boolean;
  approvedAffiliatesAmount: number;
  disapprovedAffiliatesAmount: number;
  pendingAffiliatesAmount: number;
  cantManualDelivery: any;
  inputDeliveryError: boolean;
  inputDelivery: string;
  handleClickDelivery: () => void;
  isDelivering: boolean;
  handleClickGetInvoice: () => void;
  productInvoice: any;
  isLoadingInvoice: boolean;
};

const QuickActions = ({
  productDataReceive,
  statusProduct,
  physicalProductPending,
  copiedCheckout,
  handleChange,
  inputInviteAffiliate,
  productData,
  isValid,
  handleClickInviteAffiliate,
  isInvitingAffiliate,
  errorFor,
  handleClickGetAffiliatesProductStatusSummary,
  isLoadingAffiliatesSummary,
  approvedAffiliatesAmount,
  disapprovedAffiliatesAmount,
  pendingAffiliatesAmount,
  cantManualDelivery,
  inputDeliveryError,
  inputDelivery,
  handleClickDelivery,
  isDelivering,
  handleClickGetInvoice,
  productInvoice,
  isLoadingInvoice
}: Props) => {
  const { id, has_children, parent_product, charge_type, type } = productDataReceive;

  const isBetaBatchCoupon = eduzzAccountsService.verifyUserHasBetaModule('cupom_lote');
  const hasParentProduct = parent_product !== 0;
  const oldUrl = `/producer/coupons/${hasParentProduct ? `${parent_product}/` : ''}${id}`;
  const verticalCouponUrl = `${VERTICAL_URL}/coupons?productId=${id}`;
  const couponURL = !isBetaBatchCoupon ? verticalCouponUrl : oldUrl;
  const isAffiliateBlocked = !has_children && charge_type === EChargeType.FREE;

  if (statusProduct === 'draft') {
    return (
      <>
        <h3 className='text-text-secondary mb-2 text-xs font-semibold tracking-wide uppercase'>Ações Rápidas</h3>
        <MyCollapse title='Links' icon={<LinkOutlined style={{ fontSize: 16 }} />} arrow contentActive>
          <div className='mb-1 flex items-center gap-2'>
            <InfoSolidIcon style={{ fontSize: 20, color: '#ffd700' }} />
            <span className='text-text-heading text-sm font-semibold'>Finalize o cadastro do seu produto.</span>
          </div>
          <p className='text-text-secondary text-xs leading-relaxed'>
            Falta pouco para você começar a vender na Eduzz, clique em editar e finalize o cadastro desse produto para
            habilitar o link de venda.
          </p>
        </MyCollapse>
      </>
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <h3 className='text-text-secondary mb-0 text-xs font-semibold tracking-wide uppercase'>Ações Rápidas</h3>

      {!has_children &&
        ((statusProduct && statusProduct === 'active') || statusProduct === 'inactive') &&
        !physicalProductPending && <QuickViewLinks copiedCheckout={copiedCheckout} product={productDataReceive} />}

      {(has_children || (!has_children && parent_product === 0)) &&
        ((statusProduct && statusProduct === 'active') || statusProduct === 'inactive') && (
          <Tooltip
            placement='top'
            title={isAffiliateBlocked ? 'O recurso de afiliado está indisponível para produtos gratuitos' : undefined}
          >
            <div>
              <MyCollapse
                id='invite-affiliate'
                title='Adicionar Afiliados'
                icon={<GroupOutlined style={{ fontSize: 16 }} />}
                arrow={true}
                contentActive={false}
                disabled={isAffiliateBlocked}
              >
                <CustomCopyToClipboard
                  id='invite-affiliate-link'
                  text={`${HOST_ORBITA}/affiliate/invite/${id}`}
                  disableUnderline={true}
                  style={{ width: '100%' }}
                />

                <div className='mt-2'>
                  <label htmlFor='invite-email' className='mb-1 block text-xs'>
                    Convide por E-mail
                  </label>
                  <Input
                    id='invite-email'
                    type='text'
                    onChange={e => handleChange('inputInviteAffiliate', e)}
                    value={inputInviteAffiliate}
                    autoFocus
                    status={isValid('inputInviteAffiliate') ? 'error' : undefined}
                    suffix={
                      <>
                        {!isInvitingAffiliate && (
                          <Tooltip title='Enviar'>
                            <Button
                              id='send-invite-button'
                              type='text'
                              size='small'
                              icon={<SendOutlined style={{ fontSize: 16 }} />}
                              onClick={() => handleClickInviteAffiliate()}
                            />
                          </Tooltip>
                        )}
                        {isInvitingAffiliate && <Spin size='small' />}
                      </>
                    }
                  />
                  {!isValid('inputInviteAffiliate') ? null : (
                    <small className='text-error flex items-center justify-between text-xs'>
                      {errorFor('inputInviteAffiliate')}
                    </small>
                  )}
                </div>
              </MyCollapse>
            </div>
          </Tooltip>
        )}

      {((statusProduct && statusProduct === 'active') || statusProduct === 'inactive' || statusProduct === 'filed') &&
        !productData.parent_product && (
          <MyCollapse
            title='Ver Afiliados do Produto'
            icon={<TeamOutlined style={{ fontSize: 16 }} />}
            arrow={true}
            contentActive={false}
            onClick={() => handleClickGetAffiliatesProductStatusSummary()}
          >
            {isLoadingAffiliatesSummary && (
              <div className='py-2.5 text-center'>
                <Spin />
              </div>
            )}

            {!isLoadingAffiliatesSummary && (
              <table className='w-full'>
                <thead>
                  <tr>
                    <th className='text-text-secondary w-1/3 border-none p-0 text-left text-xs font-normal'>
                      Aprovados
                    </th>
                    <th className='text-text-secondary w-1/3 border-none p-0 text-left text-xs font-normal'>
                      Reprovados
                    </th>
                    <th className='text-text-secondary w-1/3 border-none p-0 text-left text-xs font-normal'>
                      Pendentes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className='text-text-heading w-1/3 border-none p-0 text-left text-sm font-semibold'>
                      {approvedAffiliatesAmount}
                    </td>
                    <td className='text-text-heading w-1/3 border-none p-0 text-left text-sm font-semibold'>
                      {disapprovedAffiliatesAmount}
                    </td>
                    <td className='text-text-heading w-1/3 border-none p-0 text-left text-sm font-semibold'>
                      {pendingAffiliatesAmount}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
            <Link
              to={`/producer/myaffiliates?p=${id}&status=approved`}
              className='text-text-secondary hover:text-primary float-right mt-3 text-xs font-semibold uppercase no-underline'
            >
              VER TODOS OS AFILIADOS
            </Link>
          </MyCollapse>
        )}

      <Tooltip
        placement='top'
        title={
          cantManualDelivery
            ? 'Entrega manual não é permitida para esse produto. Ou não possui entrega online cadastrada'
            : undefined
        }
      >
        <div>
          <MyCollapse
            disabled={cantManualDelivery}
            title='Entrega Manual'
            icon={<SendOutlined style={{ fontSize: 16 }} />}
            arrow={true}
            contentActive={false}
          >
            <label htmlFor='delivery-manual' className='mb-1 block text-xs'>
              E-mail do Cliente
            </label>
            <Input
              id='delivery-manual'
              type='text'
              onChange={e => handleChange('inputDelivery', e)}
              status={inputDeliveryError ? 'error' : undefined}
              value={inputDelivery}
              autoFocus
              suffix={
                <>
                  {!isDelivering && !inputDeliveryError && (
                    <Tooltip title='Enviar'>
                      <Button
                        type='text'
                        size='small'
                        icon={<SendOutlined style={{ fontSize: 16 }} />}
                        onClick={handleClickDelivery}
                      />
                    </Tooltip>
                  )}
                  {!isDelivering && inputDeliveryError && (
                    <Tooltip title='Erro ao enviar'>
                      <span className='text-error text-base'>✕</span>
                    </Tooltip>
                  )}
                  {isDelivering && !inputDeliveryError && <Spin size='small' />}
                </>
              }
            />
          </MyCollapse>
        </div>
      </Tooltip>

      {((statusProduct && statusProduct === 'active') || statusProduct === 'inactive' || statusProduct === 'filed') && (
        <MyCollapse
          title='Estatísticas'
          icon={<FileTextOutlined style={{ fontSize: 16 }} />}
          arrow={true}
          contentActive={false}
          onClick={handleClickGetInvoice}
        >
          {isLoadingInvoice && (
            <div className='py-2.5 text-center'>
              <Spin />
            </div>
          )}

          {!isLoadingInvoice && (
            <table className='w-full'>
              <thead>
                <tr>
                  <th className='text-text-secondary w-1/4 border-none p-0 text-center text-xs font-normal'>Abertas</th>
                  <th className='text-text-secondary w-1/4 border-none p-0 text-center text-xs font-normal'>
                    Canceladas
                  </th>
                  <th className='text-text-secondary w-1/4 border-none p-0 text-center text-xs font-normal'>Pagas</th>
                  <th className='text-text-secondary w-1/4 border-none p-0 text-center text-xs font-normal'>
                    Reembolsadas
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className='w-1/4 border-none p-0 text-center text-sm'>{productInvoice?.open}</td>
                  <td className='w-1/4 border-none p-0 text-center text-sm'>{productInvoice?.canceled}</td>
                  <td className='text-success w-1/4 border-none p-0 text-center text-sm font-semibold'>
                    {productInvoice?.paid}
                  </td>
                  <td className='w-1/4 border-none p-0 text-center text-sm'>{productInvoice?.refunded}</td>
                </tr>
              </tbody>
            </table>
          )}
        </MyCollapse>
      )}

      <ProductChangeHistory productData={productDataReceive} />

      {((!has_children && statusProduct && statusProduct === 'active') || statusProduct === 'inactive') &&
        !(charge_type === 'A' && type === constants.PHYSICAL) &&
        charge_type !== 'L' && (
          <PermissionHide action='myeduzz:meus-produtos:gerenciar-cupons'>
            {!isBetaBatchCoupon ? (
              <a
                id='quickview-coupons-link'
                href={couponURL}
                className='text-text-secondary border-grey-100 flex w-full items-center justify-between rounded-sm border bg-white px-4 py-3 no-underline'
              >
                <span className='flex items-center gap-2 text-sm leading-none font-semibold'>
                  <DollarOutlined style={{ fontSize: 16 }} /> Cupons
                </span>
                <RightOutlined className='text-xs text-[#999]' />
              </a>
            ) : (
              <Link
                id='quickview-coupons-link'
                onClick={functions.addScrollPage}
                to={couponURL}
                className='text-text-secondary border-grey-100 flex w-full items-center justify-between rounded-sm border bg-white px-4 py-3 no-underline'
              >
                <span className='flex items-center gap-2 text-sm leading-none font-semibold'>
                  <DollarOutlined style={{ fontSize: 16 }} /> Cupons
                </span>
                <RightOutlined className='text-xs text-[#999]' />
              </Link>
            )}
          </PermissionHide>
        )}

      {((!has_children && statusProduct && statusProduct === 'active') || statusProduct === 'inactive') && (
        <Link
          id='quickview-recovery-messages-link'
          onClick={functions.addScrollPage}
          to={`/producer/rvpmessages/${id}`}
          className='text-text-secondary border-grey-100 flex w-full items-center justify-between rounded-sm border bg-white px-4 py-3 no-underline'
        >
          <span className='flex items-center gap-2 text-sm leading-none font-semibold'>
            <MessageOutlined style={{ fontSize: 16 }} />
            Mensagens de Recuperação
          </span>
          <RightOutlined className='text-xs text-[#999]' />
        </Link>
      )}

      <a
        id='quickview-showcase-link'
        onClick={() => window.open(`${URL_SELECT}/product/details/${id}/source`, '_blank')}
        className='text-text-secondary border-grey-100 flex w-full cursor-pointer items-center justify-between rounded-sm border bg-white px-4 py-3 no-underline'
      >
        <span className='flex items-center gap-2 text-sm leading-none font-semibold'>
          <AppstoreOutlined style={{ fontSize: 16 }} /> Vitrine
        </span>
        <RightOutlined className='text-xs text-[#999]' />
      </a>
    </div>
  );
};

export default QuickActions;
